import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Returns a string indicating whether to use the singular or plural form of a word based on `count`.
 *
 * @param count - The number determining if the singular or plural form should be used.
 * @param singular - The word in its singular form to return if `count` is 1.
 * @param plural - The word in its plural form to return if `count` is not 1.
 *
 * @example
 * // Singular usage:
 * console.log(singularOrPlural(1, 'apple', 'apples')); // Outputs: 'apple'
 *
 * // Plural usage:
 * console.log(singularOrPlural(5, 'apple', 'apples')); // Outputs: 'apples'
 */
export function singularOrPlural(
  count: number,
  singular: string,
  plural: string,
) {
  return count === 1 || count === 0 ? singular : plural;
}

/**
 * Generates an array of random dice rolls with the specified minimum and maximum values.
 *
 * @param {number} min - The minimum value for each roll. Default is 1.
 * @param {number} max - The maximum value for each roll. Default is 6.
 * @param {number} amount - The number of dice to roll. Default is 3.
 * @returns {number[]} An array of the generated dice rolls.
 */
export function dice(min = 1, max = 6, amount = 3) {
  const numbers: number[] = [];
  while (numbers.length < amount) {
    const randomNumber = Math.floor(Math.random() * max) + min; //NOSONAR: Gera um número entre 1 e 6
    if (!numbers.includes(randomNumber)) {
      // Verifica se o número já foi gerado
      numbers.push(randomNumber);
    }
  }
  return numbers;
}

/**
 * Format a number to Brazilian currency.
 *
 * @param {number} value The numeric value to format.
 * @return {string} The formatted value as Brazilian currency (BRL).
 */
export function formatToCurrency(value: number, fractionDigits = 2) {
  if (typeof value !== "number") {
    return "";
  }

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
}

/**
 * Normaliza uma string removendo os acentos dos caracteres.
 *
 * @param {string} str - A string a ser normalizada.
 * @returns {string} - A string normalizada sem acentos.
 *
 * @example
 * // Retorna "Cafe"
 * normalizeString("Café");
 *
 * @example
 * // Retorna "Agua"
 * normalizeString("Água");
 *
 * @description
 * A função `normalizeString` utiliza o método `normalize` do JavaScript para descompor os caracteres acentuados em caracteres base e acentos separados.
 * Em seguida, a expressão regular `/[\u0300-\u036f]/g` é usada para remover os acentos da string.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize|String.prototype.normalize()}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/RegExp|RegExp}
 */
export function normalizeString(str: string): string {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}
